<template>
  <div class="orders-grid-actions">
    <BackstageTooltip :text="tooltipText" :key="seconds">
      <BackstageIcon
        v-if="showEditIcon"
        name="edit"
        :class="
          isEditButtonEnabledForDealers
            ? 'orders-grid-actions-edit-order-icon-enabled'
            : 'orders-grid-actions-edit-order-icon-disabled'
        "
        @click="onDealerOrderEditing" />
    </BackstageTooltip>
    <BackstageIcon name="xlsx-file" class="orders-grid-actions-xlsx-download" @click="handleExportClick" />

    <Teleport to="body">
      <div v-if="showSelectableFieldsPopup">
        <NotificationMessage
          size="large"
          :show-close-button="true"
          :show-outside-close-button="false"
          class="selectable-fields-popup"
          @close="showSelectableFieldsPopup = false">
          <SelectableFields @export="handleDownloadWithSelectedFields" />
        </NotificationMessage>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import SelectableFields from '@/components/orders/selectable-fields.vue';
import { useAnalytics } from '@/composables/analytics';
import { useFormat } from '@/composables/format';
import useCounter from '@/composables/useCounter';
import { dealerOrderEditTimeLimit } from '@/constants/order-update.constants';
import { SELECTABLE_FIELDS_DISABLED_ORDER_TYPES } from '@/constants/order.constants';
import { OrderEditType } from '@/constants/orderEditType';
import { ordersMixins } from '@/mixins/orders.mixins';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import type { Order } from '@/models/order.model';
import router, { RouteNames } from '@/router';
import { FeaturesService } from '@/services/features.service';
import { OrdersService } from '@/services/orders.service';
import { useUserStore } from '@/stores/user';
import type { OrderType } from '@onrunning/backstage-models';
import { defaultSelectedLineFields } from '@onrunning/backstage-models/dist/orders/line-selectable-fields.model';
import { storeToRefs } from 'pinia';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const ordersService = new OrdersService();

const props = defineProps<{
  params: any; // Provided by ag-grid // TODO : eventually should add type for params
}>();

const { selectedAccountId: accountId } = storeToRefs(useUserStore());

const { t } = useI18n();
const { addCustomGaEvent } = useAnalytics();
const user = useUserStore();
const format = useFormat();
let showEditIcon = false;

const showSelectableFieldsPopup = ref(false);

const globalProperties = getCurrentInstance()?.appContext.config.globalProperties;

const handleExportClick = () => {
  if (FeaturesService.selectableFieldsEnabled) {
    const orderType: OrderType | undefined = props.params?.data?.type;

    if (!orderType || SELECTABLE_FIELDS_DISABLED_ORDER_TYPES.has(orderType)) {
      handleDownloadWithSelectedFields(Array.from(defaultSelectedLineFields));

      return;
    }

    showSelectableFieldsPopup.value = true;
  } else {
    handleDownloadWithSelectedFields(Array.from(defaultSelectedLineFields));
  }
};

const handleDownloadWithSelectedFields = async (selectedFields: string[]) => {
  if (props.params.colDef.field === 'actions') {
    try {
      showSelectableFieldsPopup.value = false;
      const fileName = `${props.params.data.orderNumber} ${new Date().toISOString().slice(0, 10)}.xlsx`;
      const orderNumber = props.params.data.orderNumber;

      await ordersService.downloadOrder([orderNumber], accountId.value as string, fileName, selectedFields);
    } catch (err) {
      addCustomGaEvent({
        event: GaCustomEvents.FileDownloadError,
        link_text: props.params.data.orderNumber,
      });
      globalProperties?.$snotify.warning(t('documentsCheckout.document_is_not_available'));
    }
  }
};

const onDealerOrderEditing = () => {
  router.push({
    name: RouteNames.EditOrder,
    params: {
      id: props.params.data.orderNumber,
      editType: OrderEditType.Order,
      step: AddEditOrderBreadcrumbSteps.productsModelsSelection,
    },
  });
};

const { minutes, seconds } = useCounter(props.params.data.date, dealerOrderEditTimeLimit);

const tooltipText = computed(() => {
  const formattedMinutes = format.formatTimeForTwoDigits(minutes.value);
  const formattedSeconds = format.formatTimeForTwoDigits(seconds.value);

  return t('viewOrders.edit_action_tooltip_title', {
    time: `${formattedMinutes}:${formattedSeconds}`,
  });
});

const isEditButtonEnabledForDealers = computed(() => {
  return (
    user.isDealer() &&
    ordersMixins.isOrderEditableForDealer(props.params.data as Order, user?.userProfile?.user) &&
    (minutes.value > 0 || seconds.value > 0)
  );
});

onMounted(() => {
  showEditIcon = isEditButtonEnabledForDealers.value;
});
</script>

<style lang="scss" scoped>
.orders-grid-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  &-xlsx-download {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }

  &-edit-order-icon {
    width: 14px;
    height: 14px;

    &-enabled {
      cursor: pointer;
    }

    &-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
